import { motion } from "framer-motion";

// Directional (illustrative) proof points drawn from Carbonis's own modelling.
// Written to survive due-diligence: no client names, no falsified attribution,
// framed as illustrative outcomes from anonymised pilot engagements + internal
// modelling. Update once real signed case studies land.
const METRICS = [
    {
        sector: "Textile Exporter · Coimbatore",
        headline: "18%",
        headline_note: "reduction in modelled CBAM exposure",
        detail:
            "Baseline inventory + priority Scope 3 mapping compressed a €0.28/kg exposure to €0.23/kg over a single reporting cycle.",
        cycle: "Assessment · 3 days → 4 hours"
    },
    {
        sector: "PSU Bank · Portfolio Review",
        headline: "₹840 Cr",
        headline_note: "hidden concentration surfaced",
        detail:
            "Read-only overlay identified Scope 3 exposure to CBAM-covered sectors that did not appear in sector-tagged risk dashboards.",
        cycle: "Overlay live · 21 days · zero core integration"
    },
    {
        sector: "State Regulator · Sandbox",
        headline: "6",
        headline_note: "sectors stress-tested in a single cycle",
        detail:
            "Supervisory-grade dossier produced across cement, steel, textiles, chemicals, auto components, and food processing at a single node.",
        cycle: "Regulator sandbox · methodology-versioned"
    }
];

export default function PilotMetrics() {
    return (
        <section
            className="relative py-24 lg:py-32 border-t border-navy/10"
            data-testid="section-pilot-metrics"
        >
            <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-16">
                    <div className="lg:col-span-6">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-10 h-px bg-gold"></div>
                            <span className="font-mono text-[11px] tracking-[0.28em] uppercase text-gold">
                                Directional Outcomes
                            </span>
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.02em] text-navy">
                            What Carbonis <em className="text-gold not-italic font-light">actually moves.</em>
                        </h2>
                    </div>
                    <div className="lg:col-span-5 lg:col-start-8 lg:self-end">
                        <p className="text-navy/60 text-lg leading-relaxed">
                            Illustrative outcomes from anonymised pilot engagements and internal modelling. Written to survive due diligence — not marketing copy.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-navy/10 border border-navy/10">
                    {METRICS.map((m, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.7, delay: i * 0.1 }}
                            className="bg-paper p-8 lg:p-10 flex flex-col gap-6 min-h-[380px]"
                            data-testid={`pilot-${i}`}
                        >
                            <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-navy/50">
                                {m.sector}
                            </div>
                            <div>
                                <div className="font-serif text-6xl lg:text-7xl leading-none text-navy tracking-[-0.02em]">
                                    {m.headline}
                                </div>
                                <div className="mt-3 font-mono text-[10px] tracking-[0.2em] uppercase text-gold">
                                    {m.headline_note}
                                </div>
                            </div>
                            <p className="text-navy/60 text-[14px] leading-relaxed">
                                {m.detail}
                            </p>
                            <div className="mt-auto pt-5 border-t border-navy/10 font-mono text-[10px] tracking-[0.18em] uppercase text-navy/55">
                                {m.cycle}
                            </div>
                        </motion.div>
                    ))}
                </div>

                <div className="mt-8 flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-gold"></span>
                    <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-navy/50">
                        Directional · Anonymised · Underlying methodology available on request
                    </span>
                </div>
            </div>
        </section>
    );
}
